import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import Router from 'next/router';
import { twMerge } from 'tailwind-merge';
import {
  faChevronDown,
  faHome,
  faSignIn,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { shortenHash } from '@soundxyz/utils/const';
import { useChannelsEnabled } from '@components/Channels/helpers';
import { ChannelsSection } from '@components/LeftNav/ChannelsSection';
import { Avatar } from '@components/basics/Images/Avatar';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { MOBILE_NAV_HEIGHT } from '@components/main-components/mobile-nav/constants';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useStableCallback } from '@hooks/useStableCallback';
import { trackClickEvent } from '@lib/analytics';
import { useHeaderStore } from '@store/header';
import { webappUriPath } from '@utils/url';
import { NewNavLink } from '../styles';
import { NavLinks } from './Links';
import { WalletBalance } from './WalletBalance';

export function MobileNavMenu() {
  const { authUser } = useAuth();
  const { logout } = useConnectedAuth();

  const { isLeftNavOpen, close } = useHeaderStore();

  const [openBalance, setOpenBalance] = useState(false);

  const { enabled: channelsEnabled } = useChannelsEnabled();

  const onLogout = useStableCallback(() => {
    trackClickEvent({
      event: 'Logout Clicked',
      properties: {
        sourceComponent: 'Navigation',
      },
    });
    close();
    logout();
  });

  const goHome = useStableCallback(() => {
    close();
    Router.push('/');
  });

  return (
    <div
      className={twMerge(
        'fixed inset-0 z-mobileNav flex h-screen min-h-screen transform overflow-hidden bg-opacity-25 ease-in-out',
        isLeftNavOpen
          ? 'translate-x-0 overflow-hidden opacity-100 transition-opacity duration-500'
          : 'delay-400 -translate-x-full opacity-0 transition-all',
      )}
    >
      <section
        className={twMerge(
          'shadow-xl delay-400 absolute z-overlay h-full w-[75vw] transform overflow-auto bg-white transition-all duration-500 ease-in-out',
          isLeftNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="relative flex h-full flex-col gap-6 p-4">
          <Collapsible.Root
            className="CollapsibleRoot"
            open={openBalance}
            onOpenChange={setOpenBalance}
          >
            <div className="flex items-center justify-between">
              {authUser ? (
                <AccessibleLink
                  className="flex w-full items-center gap-4"
                  pathnameUri={webappUriPath(authUser.webappUri)}
                  onClick={close}
                >
                  <Avatar
                    size={40}
                    src={authUser.avatar?.url}
                    walletAddress={authUser.publicAddress}
                    username={authUser.username}
                    webappUri={authUser.webappUri}
                    disableLink
                    borderRadius="100%"
                  />

                  <div className="flex flex-col gap-1">
                    <span className="font-title text-title-xs font-medium text-base800">
                      {authUser.username}
                    </span>
                    <span className="font-base text-[11px] font-normal leading-[11px] text-base400">
                      {shortenHash(authUser.publicAddress, 5, 4)}
                    </span>
                  </div>
                </AccessibleLink>
              ) : (
                <div className="flex w-full items-center gap-4">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-neutral200" />

                  <div className="flex flex-col gap-1">
                    <div className="bg-base h-4 w-full animate-pulse rounded-lg bg-neutral200" />
                    <div className="h-4 w-full animate-pulse rounded-lg bg-neutral200" />
                  </div>
                </div>
              )}

              <Collapsible.Trigger asChild>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="duration-50 p-1 text-base500 transition-all ease-linear group-hover:text-base900 data-[state=closed]:rotate-0 data-[state=open]:rotate-180"
                  data-state={!!openBalance ? 'open' : 'closed'}
                />
              </Collapsible.Trigger>
            </div>
            <Collapsible.Content>
              <Divider className="mt-6" />
              <WalletBalance target="mobile" variant="left-nav" />
            </Collapsible.Content>
          </Collapsible.Root>

          <div className="flex flex-col gap-3">
            <NavLinks variant="mobile" />
          </div>

          {channelsEnabled && (
            <>
              <Divider className="!mb-0" />

              <ChannelsSection />
            </>
          )}

          <div className="pb-[calc(54px+env(safe-area-inset-bottom))]" />
        </div>
      </section>

      <section
        className="h-full w-screen cursor-pointer select-none bg-black opacity-75 [-webkit-tap-highlight-color:transparent]"
        onClick={close}
      />

      <NewNavLink onClick={onLogout} className="absolute bottom-0 z-search mb-2 w-[75vw] bg-white">
        <div className="flex w-full items-center justify-between bg-white p-4">
          Logout <FontAwesomeIcon icon={faSignIn} className="text-base500" />
        </div>
      </NewNavLink>

      <NewNavLink
        onClick={goHome}
        className="absolute bottom-0 right-0 z-[2050] w-[25vw] border-l border-l-base200 bg-white"
        style={{ height: `${MOBILE_NAV_HEIGHT}px` }}
      >
        <div className="shadow-xl flex w-full flex-col items-center justify-center bg-white px-4">
          <FontAwesomeIcon icon={faHome} className="text-base600" fontSize={20} />
          <span className="font-base text-base-xs font-medium text-base600">Home</span>
        </div>
      </NewNavLink>
    </div>
  );
}
